import React, { useState } from 'react';
import SchemaForm, { FormField } from '../../components/Forms/SchemaForm';
import VerificationPageLayout from '../../components/Layout/VerificationPageLayout';
import VerificationResultDisplay from '../../components/Verification/VerificationResultDisplay';
import {
    kycOnlineService,
    kycOnlineSchema,
    kycAddressSchema,
    KYCMatchItem,
    KYCResponse,
} from '../../services/verificationSystemServices/kycOnlineService';
import {
    Box,
    Alert,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
} from '@mui/material';
import ActionButton from '../../components/Button/ActionButton';

const KYCOnline = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [matches, setMatches] = useState<KYCMatchItem[] | null>(null);
    const [fetchingResultFor, setFetchingResultFor] = useState<string | null>(null);
    const [report, setReport] = useState<KYCResponse | null>(null);

    // Address form state — shown after selecting a match
    const [selectedMatch, setSelectedMatch] = useState<KYCMatchItem | null>(null);
    const [addressLoading, setAddressLoading] = useState(false);

    // Hold Phase 1 form values for passing yourReference/voucherCode to Phase 2
    const [searchPayload, setSearchPayload] = useState<any>(null);

    // ── Phase 1: search for consumer matches ──────────────────────────────────
    const handleSubmit = async (data: any) => {
        setIsLoading(true);
        setError(null);
        setMatches(null);
        setReport(null);
        setSelectedMatch(null);

        const payload = kycOnlineService.createPayload(data);
        setSearchPayload(payload);

        try {
            const response = await kycOnlineService.getMatch(payload);

            if (response.matches.length === 1) {
                // Single match — show address form directly
                setSelectedMatch(response.matches[0]);
            } else {
                setMatches(response.matches);
            }
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || 'An error occurred during KYC match');
        } finally {
            setIsLoading(false);
        }
    };

    // ── Select a match → show address form ────────────────────────────────────
    const handleSelectMatch = (match: KYCMatchItem) => {
        setSelectedMatch(match);
        setMatches(null);
        setError(null);
    };

    // ── Phase 2: submit address form → get KYC result ─────────────────────────
    const handleAddressSubmit = async (data: any) => {
        if (!selectedMatch) return;

        setAddressLoading(true);
        setError(null);

        try {
            const response = await kycOnlineService.getResult({
                enquiryId: parseInt(selectedMatch.enquiry_id),
                enquiryResultId: parseInt(selectedMatch.enquiry_result_id),
                idNumber: selectedMatch.id_number || undefined,
                addressLine1: data.addressLine1,
                addressLine2: data.addressLine2,
                addressLine3: data.addressLine3 || undefined,
                addressLine4: data.addressLine4 || undefined,
                postalCode: data.postalCode,
                yourReference: searchPayload?.yourReference || undefined,
                voucherCode: searchPayload?.voucherCode || undefined,
            });
            setReport(response);
            setSelectedMatch(null);
        } catch (err: any) {
            setError(err.response?.data?.detail || err.message || 'Failed to fetch KYC result');
        } finally {
            setAddressLoading(false);
        }
    };

    // ── Phase 1 form schema ───────────────────────────────────────────────────
    const formSchema: FormField[] = [
        {
            name: 'idNumber',
            label: 'ID Number',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
            dependencies: ['passportNo', 'firstName', 'surname', 'birthDate'],
            maxLength: 13,
        },
        {
            name: 'passportNo',
            label: 'Passport Number',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
            dependencies: ['idNumber', 'firstName', 'surname', 'birthDate'],
        },
        {
            name: 'firstName',
            label: 'First Name',
            type: 'text',
            gridProps: { xs: 12, md: 4 },
            required: false,
            dependencies: ['idNumber', 'passportNo', 'surname', 'birthDate'],
        },
        {
            name: 'surname',
            label: 'Surname',
            type: 'text',
            gridProps: { xs: 12, md: 4 },
            required: false,
            dependencies: ['idNumber', 'passportNo', 'firstName', 'birthDate'],
        },
        {
            name: 'birthDate',
            label: 'Date of Birth',
            type: 'date',
            gridProps: { xs: 12, md: 4 },
            required: false,
            dependencies: ['idNumber', 'passportNo', 'firstName', 'surname'],
        },
        {
            name: 'voucherCode',
            label: 'Voucher Code',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
        {
            name: 'yourReference',
            label: 'Your Reference',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
    ];

    // ── Phase 2 address form schema ───────────────────────────────────────────
    const addressFormSchema: FormField[] = [
        {
            name: 'addressLine1',
            label: 'Address Line 1',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: true,
        },
        {
            name: 'addressLine2',
            label: 'Address Line 2',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: true,
        },
        {
            name: 'addressLine3',
            label: 'Address Line 3',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
        {
            name: 'addressLine4',
            label: 'Address Line 4',
            type: 'text',
            gridProps: { xs: 12, md: 6 },
            required: false,
        },
        {
            name: 'postalCode',
            label: 'Postal Code',
            type: 'text',
            gridProps: { xs: 12, md: 4 },
            required: true,
        },
    ];

    const headerCellSx = {
        fontWeight: 700,
        color: '#132F5F',
        fontSize: '0.78rem',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.04em',
    };

    return (
        <VerificationPageLayout title="KYC Online">

            {/* ── Phase 1: Search Form ── */}
            {!selectedMatch && !report && (
                <Box mb={4}>
                    <SchemaForm
                        schema={formSchema}
                        onSubmit={handleSubmit}
                        submitLabel={isLoading ? 'Searching…' : 'Search'}
                        validationSchema={kycOnlineSchema}
                    />
                </Box>
            )}

            {/* ── Loading spinner ── */}
            {(isLoading || addressLoading) && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                    <CircularProgress sx={{ color: '#2D4677' }} />
                </Box>
            )}

            {/* ── Error banner ── */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* ── Multiple matches — let the user pick ── */}
            {matches && matches.length > 1 && !report && !selectedMatch && (
                <Box mb={4}>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                        Multiple consumers matched your search. Select the correct record to proceed with KYC verification.
                    </Typography>
                    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #dce6f5', borderRadius: '12px', overflow: 'hidden' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#EFF3FB' }}>
                                    <TableCell sx={headerCellSx}>Consumer ID</TableCell>
                                    <TableCell sx={headerCellSx}>Full Name</TableCell>
                                    <TableCell sx={headerCellSx}>ID Number</TableCell>
                                    <TableCell sx={headerCellSx}>Date of Birth</TableCell>
                                    <TableCell sx={headerCellSx}>Gender</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {matches.map((match) => (
                                    <TableRow key={match.enquiry_result_id} hover sx={{ '&:last-child td': { borderBottom: 'none' } }}>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.consumer_id ?? '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1a1a2e' }}>
                                            {[match.first_name, match.surname].filter(Boolean).join(' ') || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.id_number ?? '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.birth_date ?? '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>{match.gender ?? '—'}</TableCell>
                                        <TableCell align="right">
                                            <ActionButton
                                                size="small"
                                                onClick={() => handleSelectMatch(match)}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    py: 0.75,
                                                    px: 2,
                                                }}
                                            >
                                                Select
                                            </ActionButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* ── Address Form (shown after selecting a match) ── */}
            {selectedMatch && !report && !addressLoading && (
                <Box mb={4}>
                    <Paper
                        elevation={0}
                        sx={{
                            border: '1px solid #dce6f5',
                            borderRadius: '12px',
                            p: 3,
                            mb: 3,
                            backgroundColor: '#FAFBFE',
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 700,
                                color: '#0A1D56',
                                fontSize: '0.85rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                mb: 0.5,
                            }}
                        >
                            Selected Consumer
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4a5568', fontSize: '0.82rem' }}>
                            {[selectedMatch.first_name, selectedMatch.surname].filter(Boolean).join(' ')}
                            {selectedMatch.id_number ? ` — ID: ${selectedMatch.id_number}` : ''}
                        </Typography>
                    </Paper>

                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 700,
                            color: '#0A1D56',
                            fontSize: '0.85rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            mb: 2,
                        }}
                    >
                        Enter Address Details for KYC Verification
                    </Typography>

                    <SchemaForm
                        schema={addressFormSchema}
                        onSubmit={handleAddressSubmit}
                        submitLabel="Verify KYC"
                        validationSchema={kycAddressSchema}
                    />

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { setSelectedMatch(null); setMatches(null); }}
                        sx={{
                            mt: 2,
                            borderColor: '#2D4677',
                            color: '#2D4677',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { borderColor: '#1a365d', backgroundColor: 'rgba(45,70,119,0.04)' },
                        }}
                    >
                        ← Back to Search
                    </Button>
                </Box>
            )}

            {/* ── Final report ── */}
            {report && !isLoading && !addressLoading && (
                <>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { setReport(null); setMatches(null); setSelectedMatch(null); }}
                        sx={{
                            mb: 2,
                            borderColor: '#2D4677',
                            color: '#2D4677',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { borderColor: '#1a365d', backgroundColor: 'rgba(45,70,119,0.04)' },
                        }}
                    >
                        ← New Search
                    </Button>
                    <VerificationResultDisplay
                        title="KYC Online"
                        requestId={report.log_id}
                        success={report.success}
                        message={report.message}
                        data={report.report_data ?? null}
                    />
                </>
            )}
        </VerificationPageLayout>
    );
};

export default KYCOnline;
